import { type ReactElement } from 'react';
import { Button, EmptyState, Notice, Stack, Timeline, type TimelineEntry } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { LoadingBlock } from './loading-block';

/**
 * A record's event history inside a drawer, as a vertical timeline (newest first, as the caller sorts it). A drawer is narrower than a
 * table needs, so a history never goes in a `ListTable` there: each event is a title, its reason and one mono time-and-actor line.
 * `entries` is `undefined` while it loads; `error` shows the failure with a retry above whatever was already read.
 */
export function HistoryTimeline({ entries, error, onRetry, emptyTitle }: {
  readonly entries: readonly TimelineEntry[] | undefined;
  readonly error?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
  readonly emptyTitle: string;
}): ReactElement {
  const { t } = useT();
  return (
    <Stack gap={3}>
      {error !== undefined && (
        <Notice tone="danger">
          <Stack gap={2}>
            <span>{error}</span>
            {onRetry !== undefined && <Button size="sm" variant="secondary" onClick={onRetry}>{t('ui.retry')}</Button>}
          </Stack>
        </Notice>
      )}
      {entries === undefined ? (
        error === undefined ? <LoadingBlock lines={4} /> : null
      ) : entries.length === 0 ? (
        error === undefined ? <EmptyState title={emptyTitle} /> : null
      ) : (
        <Timeline entries={entries} />
      )}
    </Stack>
  );
}
